import { getApiBaseUrl } from "@/shared/lib/apiClient";
import { getAuthToken } from "../auth";
import { workContextHeaders } from "@/modules/safecheck/workContext";

async function fetchInvoiceFileBlob(invoiceId: number, download = false): Promise<Blob> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Connexion requise pour accéder au document.");
  }

  const qs = download ? "?download=1" : "";
  const response = await fetch(`${getApiBaseUrl()}/invoices/${invoiceId}/file${qs}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...workContextHeaders(),
      Accept: "application/pdf,image/*,*/*",
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Document introuvable.");
  }

  return response.blob();
}

export async function openInvoiceFile(invoiceId: number): Promise<void> {
  const blob = await fetchInvoiceFileBlob(invoiceId, false);
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (!popup) {
    URL.revokeObjectURL(url);
    throw new Error("Autorisez les pop-ups pour afficher le document.");
  }
  // Libération différée (le viewer a besoin de l'URL un moment)
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Ouvre la pièce jointe et déclenche l'impression navigateur (pas l'écran React). */
export async function printInvoiceFile(invoiceId: number): Promise<void> {
  const blob = await fetchInvoiceFileBlob(invoiceId, false);
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (!popup) {
    URL.revokeObjectURL(url);
    throw new Error("Autorisez les pop-ups pour imprimer le document.");
  }

  const triggerPrint = () => {
    try {
      popup.focus();
      popup.print();
    } catch {
      // Certains viewers PDF bloquent print() — l'onglet reste ouvert.
    }
  };

  popup.addEventListener("load", triggerPrint);
  window.setTimeout(triggerPrint, 800);
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function downloadInvoiceFile(invoiceId: number, fileName?: string): Promise<void> {
  const blob = await fetchInvoiceFileBlob(invoiceId, true);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || `facture-${invoiceId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function getInvoiceFilePreviewUrl(invoiceId: number): Promise<string> {
  const blob = await fetchInvoiceFileBlob(invoiceId, false);
  return URL.createObjectURL(blob);
}
